import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import notify from '@/lib/notify';
import { Dialog, Transition } from '@headlessui/react';
// Importer les types API et le type FormData
import { BlockerRead } from '../../types';
import { BlockerFormData } from '../../hooks/useCadrageBlockers';
import SimpleTextarea from '../Forms/SimpleTextarea';

interface BlockerEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  blockerToEdit: BlockerRead | null; // Utiliser BlockerRead
  onSave: (formData: BlockerFormData) => void; // Envoyer BlockerFormData
  isAdding: boolean;
  // cadrageId: string; // Temporairement commenté car non utilisé
}

const BlockerEditModal: React.FC<BlockerEditModalProps> = ({
  isOpen,
  onClose,
  blockerToEdit,
  onSave,
  isAdding,
  // cadrageId,
}) => {
  const { t } = useTranslation();
  // États locaux pour les champs du formulaire
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [criticality, setCriticality] = useState<string | null>(null); // Utiliser le bon type
  const [mitigationStrategy, setMitigationStrategy] = useState('');

  // Initialise le formulaire
  useEffect(() => {
    if (isOpen) { // Réinitialiser à chaque ouverture
      if (blockerToEdit && !isAdding) {
        setName(blockerToEdit.description || ''); // Utiliser description comme nom temporairement
        setDescription(blockerToEdit.description || '');
        setCriticality(blockerToEdit.criticite || null);
        setMitigationStrategy(blockerToEdit.strategie_mitigation || '');
      } else {
        // Réinitialise pour l'ajout
        setName('');
        setDescription('');
        setCriticality(null);
        setMitigationStrategy('');
      }
    }
  }, [blockerToEdit, isOpen, isAdding]);

  const handleSave = () => {
    // Construire l'objet BlockerFormData à partir de l'état local
    const formData: BlockerFormData = {
      name: name.trim(),
      description: description.trim() || null,
      criticality: criticality,
      mitigation_strategy: mitigationStrategy.trim() || null,
    };

    // Valider que le nom n'est pas vide (requis par BlockerCreate)
    if (!formData.name.trim()) {
        notify.warning(t('blocker.modal.nameRequired'));
        return;
    }

    onSave(formData); // Envoyer BlockerFormData au parent
    // La fermeture est gérée par le parent (BlockerManager) en cas de succès
    // onClose(); // Ne pas fermer ici en cas d'erreur potentielle
  };


  const criticiteOptions = [
    { value: 'null', label: t('blocker.criticality.notDefined') },
    { value: 'faible', label: t('blocker.criticality.low') },
    { value: 'moyenne', label: t('blocker.criticality.medium') },
    { value: 'haute', label: t('blocker.criticality.high') },
  ];

  // Convertit la valeur du select (string) en string | null
  const handleCriticiteChange = (selectedValue: string) => {
    setCriticality(selectedValue === 'null' ? null : selectedValue);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-card border border-border p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium text-card-foreground mb-4"
                >
                  {isAdding ? t('blocker.modal.addTitle') : t('blocker.modal.editTitle')}
                </Dialog.Title>
                <div className="mt-2 space-y-4">
                  {/* Ajouter le champ Nom */}
                   <div>
                     <label htmlFor="blocker-name" className="block text-sm font-medium text-foreground mb-1">
                       {t('blocker.modal.nameLabel')} <span className="text-destructive">*</span>
                     </label>
                     <input
                       type="text"
                       id="blocker-name"
                       value={name}
                       onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                       required
                       className="block w-full px-3 py-2 border border-border rounded-md shadow-sm bg-background text-foreground focus:outline-none focus:ring-ring focus:border-ring sm:text-sm"
                     />
                   </div>
                  <div>
                    <label htmlFor="blocker-description" className="block text-sm font-medium text-foreground mb-1">
                      {t('blocker.modal.descriptionLabel')}
                     </label>
                     <SimpleTextarea
                       id="blocker-description"
                       value={description}
                       onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                       rows={3}
                       className="w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="blocker-criticite" className="block text-sm font-medium text-foreground mb-1">
                      {t('blocker.criticality.label')}
                     </label>
                     <select
                       id="blocker-criticite"
                       value={criticality === null ? 'null' : criticality}
                       onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleCriticiteChange(e.target.value)}
                       className="block w-full px-3 py-2 border border-border bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-ring focus:border-ring sm:text-sm"
                     >
                      {criticiteOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="blocker-mitigation" className="block text-sm font-medium text-foreground mb-1">
                      {t('blocker.modal.mitigationLabel')}
                    </label>
                    <SimpleTextarea
                      id="blocker-mitigation"
                      value={mitigationStrategy}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMitigationStrategy(e.target.value)}
                      rows={4}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onClick={onClose}
                  >
                    {t('global.cancel')}
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onClick={handleSave}
                    disabled={!name.trim()} // Désactive si le nom est vide
                  >
                    {t('global.save')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BlockerEditModal;
